import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { selectAreCurrentFolderChildrenLoaded, selectCurrentFolderId } from '@/user_files/folders/store/selectors'
import { type DriveListSort, driveListSortsEqual } from '@/user_files/sorting'

/**
 * Refreshes child folders and files on Paxton tab entry, returning to the browser tab, folder
 * navigation, and sort changes.
 *
 * Uploads, moves, and deletes can also invalidate children while this page stays mounted.
 * Track transitions so invalidation refreshes children, but load completion does not request
 * another refresh and initial loading does not duplicate the entry request.
 */
export function useDriveListFetches(sort: DriveListSort, active: boolean): void {
  const dispatch = useAppDispatch()
  // Both the id and the flag come from the same place. Taking the id as a parameter would let a
  // caller pass one folder while this subscribes to another folder's loaded flag.
  const currentFolderId = useAppSelector(selectCurrentFolderId)
  const childrenLoaded = useAppSelector(selectAreCurrentFolderChildrenLoaded)
  const previousListingRef = useRef<{ folderId: string | null; sort: DriveListSort; childrenLoaded: boolean; active: boolean }>()

  useEffect(() => {
    const previousListing = previousListingRef.current
    previousListingRef.current = { folderId: currentFolderId, sort, childrenLoaded, active }
    if (!active || !currentFolderId) return

    const becameActive = !previousListing?.active
    const listingChanged = previousListing?.folderId !== currentFolderId || !driveListSortsEqual(previousListing?.sort, sort)
    const childrenInvalidated = previousListing?.childrenLoaded && !childrenLoaded
    if (becameActive || listingChanged || childrenInvalidated) {
      dispatch(UserFoldersActions.fetchFolderChildren({ folderId: currentFolderId, sort }))
    }
    if (becameActive || listingChanged) {
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId: currentFolderId, sort }))
    }
  }, [active, currentFolderId, childrenLoaded, sort, dispatch])

  useEffect(() => {
    if (!active || !currentFolderId) return

    const refreshWhenVisible = () => {
      if (document.hidden) return
      dispatch(UserFoldersActions.fetchFolderChildren({ folderId: currentFolderId, sort }))
      dispatch(UserFilesActions.fetchFilesForFolder({ folderId: currentFolderId, sort }))
    }
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => document.removeEventListener('visibilitychange', refreshWhenVisible)
  }, [active, currentFolderId, sort, dispatch])
}
